<template>
  <v-card tile width="380px">
    <v-toolbar dense flat color="primary" class="tool-bar">
      <div class="body-2 white--text mr-2">
        <h2>天気予報</h2>
      </div>
      <v-btn
        v-if="!isMobile"
        small
        depressed
        color="success"
        @click="dialog = true"
        >詳細をみる</v-btn
      >
      <v-spacer />
      <v-btn class="mr-1" dark icon small @click="close()">
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>
    <v-row
      no-gutters
      align="center"
      justify="center"
      class="fill-height"
      v-if="!tableData"
    >
      <div>
        <v-progress-circular indeterminate></v-progress-circular>
        Loading...
      </div>
    </v-row>
    <v-data-table
      v-else
      :headers="headers"
      :items="tableData"
      :items-per-page="-1"
      :hide-default-footer="true"
      dense
      fixed-header
      height="calc(100dvh - 48px)"
      class="weather-table"
      disable-sort
    >
      <template v-slot:item.weather="{ item }">
        <v-tooltip v-if="item.weather" bottom>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-img
                :src="
                  require(`@/assets/images/weather-icons/nhk/${item.weather}.png`)
                "
                height="20px"
                contain
              ></v-img>
            </div>
          </template>
          <span>{{ getTooltip(item, "weather") }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.temperatures="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              {{ item.temperatures }}
            </div>
          </template>
          <span>{{ getTooltip(item, "temperatures") }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.rain="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on" style="font-weight: bold">
              <span :style="{ color: item.rainColor }">{{ item.rain }}</span>
            </div>
          </template>
          <span>{{ getTooltip(item, "rain") }}</span>
        </v-tooltip>
      </template>
      <template v-slot:item.windDirection="{ item }">
        <v-tooltip v-if="item.windDirection" bottom>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              <v-icon
                small
                class="outlined"
                :color="getWindColor(item.windSpeed)"
                :style="
                  `transform: rotate(${(360 / 16) * item.windDirection -
                    180}deg)`
                "
              >
                mdi-navigation
              </v-icon>
            </div>
          </template>
          <span v-for="site in ['Nhk', 'Ten', 'Wni']" :key="site">
            {{ site.toUpperCase() }} :
            <v-icon
              small
              class="outlined"
              :color="getWindColor(item.windSpeed)"
              :style="
                `transform: rotate(${(360 / 16) * item['windDirection' + site] -
                  180}deg)`
              "
            >
              mdi-navigation
            </v-icon>
          </span>
        </v-tooltip>
      </template>
      <template v-slot:item.windSpeed="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <div v-bind="attrs" v-on="on">
              {{ item.windSpeed }}
            </div>
          </template>
          <span>{{ getTooltip(item, "windSpeed") }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
    <v-dialog v-model="dialog" max-width="1200px">
      <weather-detail
        :table-data="tableData"
        :weather-date="weatherDate"
        @close="dialog = false"
      ></weather-detail>
    </v-dialog>
  </v-card>
</template>
<script>
import moment from "moment";
import _ from "lodash";
import WeatherDetail from "@/components/WeatherDetail.vue";
import * as axiosHelper from "../utils/axiosHelper";
const DATE_FORMAT = "YYYY/MM/DD HH:mm";
export default {
  name: "Weather",
  components: { WeatherDetail },
  data() {
    return {
      headers: [
        {
          text: "時刻",
          align: "center",
          value: "time"
        },
        { text: "天気", value: "weather", align: "center" },
        { text: "気温\n(℃)", value: "temperatures", align: "center" },
        { text: "降雨量\n(mm/h)", value: "rain", align: "right" },
        { text: "風向", value: "windDirection", align: "center" },
        { text: "風速\n(m/s)", value: "windSpeed", align: "right" }
      ],
      tableData: null,
      dialog: false,
      weatherDate: null
    };
  },
  watch: {
    baseDate(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.loadData();
      }
    }
  },
  computed: {
    baseDate() {
      return this.$store.getters.baseDate;
    },
    isMobile() {
      return this.$route.path === "/mobile";
    }
  },
  async created() {
    const weatherData = await this.loadData();
    const nhk = weatherData.nhk;
    const ten = weatherData.ten;
    const wni = weatherData.wni;
    this.weatherDate = {
      nhkDate: moment
        .utc(weatherData.nhkDate, DATE_FORMAT)
        .local()
        .format(DATE_FORMAT),
      tenDate: moment
        .utc(weatherData.tenDate, DATE_FORMAT)
        .local()
        .format(DATE_FORMAT),
      wniDate: moment
        .utc(weatherData.wniDate, DATE_FORMAT)
        .local()
        .format(DATE_FORMAT)
    };

    const tableData = nhk.data.map(objA => {
      const temp = {
        weather: null,
        atmospheric_temperature: null,
        rainfall_1hour_millimeters: null,
        wind_speed_meters_per_second: null,
        wind_direction_code: null
      };

      let matchingObjB = ten
        ? ten.data.find(objB => objB.time === objA.time)
        : temp;
      if (!matchingObjB) {
        matchingObjB = temp;
      }

      let matchingObjC = wni
        ? wni.data.find(objC => objC.time === objA.time)
        : temp;
      if (!matchingObjC) {
        matchingObjC = temp;
      }

      const row = {
        time: moment
          .utc(objA.time)
          .local()
          .format("MM/DD HH:mm"),
        weather: null,
        temperatures: null,
        rain: null,
        rainColor: null,
        windSpeed: null,
        windDirection: null,
        weatherNhk: null,
        temperaturesNhk: null,
        rainNhk: null,
        rainColorNhk: null,
        windSpeedNhk: null,
        windDirectionNhk: null,
        weatherTen: null,
        temperaturesTen: null,
        rainTen: null,
        rainColorTen: null,
        windSpeedTen: null,
        windDirectionTen: null,
        weatherWni: null,
        temperaturesWni: null,
        rainWni: null,
        rainColorWni: null,
        windSpeedWni: null,
        windDirectionWni: null
      };

      if (objA) {
        row.weatherNhk = objA["weather"];
        row.temperaturesNhk = objA["atmospheric_temperature"];
        row.rainNhk = objA["rainfall_1hour_millimeters"];
        row.rainColorNhk = this.getRainColor(
          matchingObjB["rainfall_1hour_millimeters"]
        );
        row.windSpeedNhk = objA["wind_speed_meters_per_second"];
        row.windDirectionNhk = objA["wind_direction_code"];
      }
      if (matchingObjB) {
        row.weatherTen = matchingObjB["weather"];
        row.temperaturesTen = matchingObjB["atmospheric_temperature"];
        row.rainTen = matchingObjB["rainfall_1hour_millimeters"];
        row.rainColorTen = this.getRainColor(
          matchingObjB["rainfall_1hour_millimeters"]
        );
        row.windSpeedTen = matchingObjB["wind_speed_meters_per_second"];
        row.windDirectionTen = matchingObjB["wind_direction_code"];
      }
      if (matchingObjC) {
        row.weatherWni = matchingObjC["weather"];
        row.temperaturesWni = matchingObjC["atmospheric_temperature"];
        row.rainWni = matchingObjC["rainfall_1hour_millimeters"];
        row.rainColorWni = this.getRainColor(
          matchingObjB["rainfall_1hour_millimeters"]
        );
        row.windSpeedWni = matchingObjC["wind_speed_meters_per_second"];
        row.windDirectionWni = matchingObjC["wind_direction_code"];
      }

      if (matchingObjB || matchingObjC) {
        const weather = this.mostFrequentValue(
          objA["weather"],
          matchingObjB["weather"],
          matchingObjC["weather"]
        );
        const temperatures = _.mean([
          objA["atmospheric_temperature"],
          matchingObjB["atmospheric_temperature"],
          matchingObjC["atmospheric_temperature"]
        ]).toFixed(2);
        const rain = _.mean([
          objA["rainfall_1hour_millimeters"],
          matchingObjB["rainfall_1hour_millimeters"],
          matchingObjC["rainfall_1hour_millimeters"]
        ]).toFixed(2);
        const [windSpeed, windDirection] = this.calcWind([
          {
            speed: objA["wind_speed_meters_per_second"],
            direction: objA["wind_direction_code"]
          },
          {
            speed: matchingObjB["wind_speed_meters_per_second"],
            direction: matchingObjB["wind_direction_code"]
          },
          {
            speed: matchingObjC["wind_speed_meters_per_second"],
            direction: matchingObjC["wind_direction_code"]
          }
        ]);

        row.weather = weather;
        row.temperatures = temperatures;
        row.rain = rain;
        row.rainColor = this.getRainColor(rain);
        row.windSpeed = windSpeed;
        row.windDirection = windDirection;
      }

      return row;
    });
    this.tableData = tableData.filter(row => {
      for (const field in row) {
        if (row[field] === -9999) {
          return false;
        }
      }
      return true;
    });
  },
  methods: {
    async loadData() {
      const url =
        "https://jxkf9fek8f.execute-api.ap-northeast-1.amazonaws.com/prod/api/weather";
      return await axiosHelper.getOutsideApi(url, {
        date: this.baseDate.utc().format("YYYY/MM/DD HH:mm")
      });
    },
    calcWind(winds) {
      const coords = [];

      winds.forEach(wind => {
        if (wind.speed !== 0 && !wind.speed) {
          return;
        }

        const y =
          Math.cos(((wind.direction * 22.5) / 180) * Math.PI) * wind.speed;
        const x =
          Math.sin(((wind.direction * 22.5) / 180) * Math.PI) * wind.speed;

        coords.push({ y, x });
      });

      const avgX = _.mean(_.map(coords, "x"));
      const avgY = _.mean(_.map(coords, "y"));

      const windSpeed = Math.sqrt(
        Math.pow(avgX, 2) + Math.pow(avgY, 2)
      ).toFixed(2);
      let windDirection =
        avgX === 0 ? 0 : (Math.atan2(avgX, avgY) * 180) / Math.PI;
      windDirection = windDirection < 0 ? 360 + windDirection : windDirection;
      windDirection = Math.round(windDirection / 22.5);
      if (windDirection === 0) {
        windDirection = 16;
      }

      return [windSpeed, windDirection];
    },
    mostFrequentValue(param1, param2, param3) {
      const countMap = new Map();

      [param1, param2, param3].forEach(param => {
        countMap.set(param, (countMap.get(param) || 0) + 1);
      });

      let maxCount = 0;
      let mostFrequent;

      countMap.forEach((count, value) => {
        if (value !== 0 && !value) {
          return;
        }
        if (count > maxCount) {
          maxCount = count;
          mostFrequent = value;
        }
      });

      return mostFrequent;
    },
    getTooltip(o, key) {
      return `NHK: ${o[key + "Nhk"]}, Tenkijp: ${o[key + "Ten"]}, WNI: ${
        o[key + "Wni"]
      }`;
    },
    close() {
      this.$store.commit("SET_IS_WEATHER", false);
    },
    getRainColor(rain) {
      const value = parseFloat(rain);
      const colors = [
        { code: "#800080", value: 80 },
        { code: "#ff3700", value: 50 },
        { code: "#D88100", value: 30 },
        { code: "#1E42AF", value: 20 },
        { code: "#2a7aaa", value: 1 },
        { code: "#888888", value: 0 }
      ];

      let current;
      for (const color of colors) {
        if (color.value <= value) {
          current = color.code;
          break;
        }
      }
      return current;
    },
    getWindColor(speed) {
      const value = speed;
      const colors = [
        { color: "#b40068", value: 25 },
        { color: "#f12705", value: 20 },
        { color: "#f49904", value: 15 },
        { color: "#f9f503", value: 10 },
        { color: "#3e42ff", value: 5 },
        { color: "#b9ebfa", value: 0 }
      ];

      let current;
      for (const color of colors) {
        if (color.value <= value) {
          current = color.color;
          break;
        }
      }
      return current;
    }
  }
};
</script>

<style>
.tool-bar-title {
  font-size: 24px;
  font-weight: bold;
  color: #5f5f5f;
  margin: 0 10px;
}
.tool-bar {
  width: 380px;
}

.weather-table th {
  font-size: 14px !important;
}
.weather-table td {
  font-size: 12px !important;
}
.weather-table th,
.weather-table td {
  padding: 0 10px !important;
}
.weather-table .v-data-table__wrapper::-webkit-scrollbar {
  width: 15px !important;
  display: block !important;
  background: #e6e6e6;
  border-left: 1px solid #dadada;
}
.weather-table .v-data-table__wrapper::-webkit-scrollbar-thumb {
  background: #b0b0b0;
  border: solid 3px #e6e6e6;
  border-radius: 7px;
}

.v-application .weather-table th {
  background-color: #5560a1 !important;
  color: white !important;
  white-space: pre-line;
  text-align: center !important;
}

.v-data-table > .v-data-table__wrapper .v-data-table__mobile-row {
  min-height: auto !important;
}
</style>
